import React from 'react';
import PropTypes from 'prop-types';

import { IComponent } from '../../../typings/generic-component';
import { makeClassNames, IClassNames } from '../../../utils/styles';
import { propTypeSkeleton, SkeletonArrayValues } from '../../../typings/skeleton';

import { setSkeletonArrayOrValue } from './skeleton-utils';
import './skeleton-wrapper.scss';

export interface ISkeletonWrapper extends IComponent {
  readonly height?: string | SkeletonArrayValues;
  readonly isCentered?: boolean;
  readonly isFlex?: boolean;
  readonly isGrid?: boolean;
  readonly gridColumns?: string | SkeletonArrayValues;
  readonly gridRows?: string | SkeletonArrayValues;
}

const classNames = (isGrid: boolean, isFlex: boolean, isCentered: boolean): IClassNames =>
  makeClassNames({
    SkeletonWrapper: [
      'skeleton-wrapper',
      isGrid && 'skeleton-wrapper--grid',
      isFlex && 'skeleton-wrapper--flex',
      isCentered && 'skeleton-wrapper--centered',
    ],
    SkeletonWrapperHeight: 'skeleton-wrapper--height',
    SkeletonWrapperGridColumns: 'skeleton-wrapper--grid-columns',
    SkeletonWrapperGridRows: 'skeleton-wrapper--grid-rows',
  });

const SkeletonWrapper: React.FC<ISkeletonWrapper> = ({
  children,
  height,
  isCentered = false,
  isFlex = false,
  isGrid = false,
  gridColumns,
  gridRows,
}) => {
  const cn = classNames(isGrid, isFlex, isCentered);
  const skeletonWrapperHeight = height ? { ...setSkeletonArrayOrValue('height', height) } : {};
  const skeletonWrapperGridColumns = gridColumns
    ? { ...setSkeletonArrayOrValue('gridTemplateColumns', gridColumns) }
    : {};
  const skeletonWrapperGridRows = gridRows ? { ...setSkeletonArrayOrValue('gridTemplateRows', gridRows) } : {};

  const classNamesList = [
    cn.SkeletonWrapper,
    Array.isArray(height) ? cn.SkeletonWrapperHeight : '',
    Array.isArray(gridColumns) ? cn.SkeletonWrapperGridColumns : '',
    Array.isArray(gridRows) ? cn.SkeletonWrapperGridRows : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classNamesList}
      style={{ ...skeletonWrapperHeight, ...skeletonWrapperGridColumns, ...skeletonWrapperGridRows }}
    >
      {children}
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  SkeletonWrapper.propTypes = {
    children: PropTypes.node,
    gridColumns: (props, propName) => propTypeSkeleton(props, propName),
    gridRows: (props, propName) => propTypeSkeleton(props, propName),
    height: (props, propName) => propTypeSkeleton(props, propName),
    isCentered: PropTypes.bool,
    isFlex: PropTypes.bool,
    isGrid: PropTypes.bool,
  };
}

export default SkeletonWrapper;
